import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { type MutableRefObject, useCallback, useMemo, useState } from "react";

/**
 * A stable stand-in for an optional consumer handler (`onValueChange`,
 * `onBlur`): the same function on every render, calling the latest handler,
 * so an inline arrow at the call site does not invalidate the root's memos
 * and context. Stays `undefined` while the handler is, for callers that
 * branch on its presence.
 */
export const useLatestCallback = <Args extends unknown[]>(
    callback: ((...args: Args) => void) | undefined,
): ((...args: Args) => void) | undefined => {
    const ref = useLatestRef(callback);
    const stableCallback = useCallback(
        (...args: Args) => {
            ref.current?.(...args);
        },
        [ref],
    );
    return callback === undefined ? undefined : stableCallback;
};

/**
 * Keeps the previous object while every own value is `Object.is` the new one,
 * so an inline literal (`flyoutDefaults={{ searchable: true }}`) does not
 * invalidate memos; function members still need a stable identity.
 */
export const useShallowStable = <T extends object | undefined>(value: T): T => {
    const [previous, setPrevious] = useState(value);
    if (isShallowEqual(previous, value)) {
        return previous;
    }
    setPrevious(value);
    return value;
};

const isShallowEqual = <T extends object | undefined>(previous: T, value: T) =>
    previous === value ||
    (previous !== undefined &&
        value !== undefined &&
        Object.keys(previous).length === Object.keys(value).length &&
        Object.entries(value).every(
            ([key, entry]) =>
                Object.prototype.hasOwnProperty.call(previous, key) &&
                Object.is(Reflect.get(previous, key), entry),
        ));

/**
 * Per-key callbacks with stable identities: `forKey(value)` returns the same
 * function for the same key across renders, calling the latest `callback`.
 * Lets memoised rows keep their props referentially equal.
 */
export const usePerKeyCallback = <K, Args extends unknown[]>(
    callback: (key: K, ...args: Args) => void,
) => {
    const ref = useLatestRef(callback);
    return useMemo(() => createPerKeyCallbacks(ref), [ref]);
};

const createPerKeyCallbacks = <K, Args extends unknown[]>(
    ref: MutableRefObject<(key: K, ...args: Args) => void>,
) => {
    const cache = new Map<K, (...args: Args) => void>();
    return (key: K) => {
        const cached = cache.get(key);
        if (cached !== undefined) {
            return cached;
        }
        const created = (...args: Args) => ref.current(key, ...args);
        cache.set(key, created);
        return created;
    };
};
